import React from 'react';
import { motion } from 'framer-motion';
import { ServerIcon, ShieldCheckIcon, ZapIcon, ScalingIcon, ArrowRightIcon, PhoneCallIcon, LockIcon, ActivityIcon } from 'lucide-react';
export function PRITrunksPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div className="bg-white" data-id="element-1775">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-1776">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-1777"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-1778"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1779">
          <div className="max-w-3xl" data-id="element-1780">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-1781">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1782">
                <span className="flex h-2 w-2 rounded-full bg-purple-400 mr-2" data-id="element-1783"></span>
                High-Capacity Voice Solutions
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-1784">
                PRI Trunking
                <br data-id="element-1785" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-1786">
                  Enterprise Voice
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8" data-id="element-1787">
                For businesses that require dedicated, high-capacity voice
                communications. PRI Trunks offer robust connections, enhanced
                security, and guaranteed quality of service.
              </p>
              <button onClick={() => onNavigate('contact')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-1788">
                Request a Consultation
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1789" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-white" data-id="element-1790">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1791">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-1792">
            {[{
            title: 'Dedicated Bandwidth',
            desc: 'PRI provides a dedicated physical connection for your voice traffic, ensuring crystal-clear quality without internet interference.',
            icon: ZapIcon,
            color: 'text-purple-500',
            bg: 'bg-purple-50'
          }, {
            title: 'Enhanced Security',
            desc: 'As a dedicated circuit, PRI offers a higher level of security compared to public internet-based solutions.',
            icon: LockIcon,
            color: 'text-blue-500',
            bg: 'bg-blue-50'
          }, {
            title: 'Reliability',
            desc: 'Proven technology with a long track record of stability and performance in enterprise environments.',
            icon: ShieldCheckIcon,
            color: 'text-emerald-500',
            bg: 'bg-emerald-50'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="flex flex-col items-start" data-id="element-1793">
                <div className={`w-14 h-14 ${feature.bg} rounded-2xl flex items-center justify-center mb-6`} data-id="element-1794">
                  <feature.icon className={`w-7 h-7 ${feature.color}`} data-id="element-1795" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1796">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-1797">{feature.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Technical Details */}
      <section className="py-24 bg-slate-50" data-id="element-1798">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1799">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1800">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1801">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-1802">
                Enterprise-Grade Performance
              </h2>
              <p className="text-lg text-slate-600 mb-8" data-id="element-1803">
                PRI (Primary Rate Interface) is the standard for large-scale
                business communications, providing 23 voice channels over a
                single T1 line.
              </p>
              <div className="space-y-6" data-id="element-1804">
                {[{
                title: '23 Concurrent Voice Channels',
                icon: PhoneCallIcon
              }, {
                title: 'Caller ID & DID Support',
                icon: ActivityIcon
              }, {
                title: 'Dedicated T1 Connection',
                icon: ServerIcon
              }, {
                title: '99.999% Reliability',
                icon: ShieldCheckIcon
              }].map((item, i) => <div key={i} className="flex items-center gap-4" data-id="element-1805">
                    <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center" data-id="element-1806">
                      <item.icon className="w-5 h-5 text-purple-600" data-id="element-1807" />
                    </div>
                    <span className="font-bold text-slate-900" data-id="element-1808">
                      {item.title}
                    </span>
                  </div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="rounded-3xl overflow-hidden shadow-2xl" data-id="element-1809">
              <img src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&h=600&fit=crop" alt="Network infrastructure" className="w-full h-auto" data-id="element-1810" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary" data-id="element-1811">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1812">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1813">
            Need a High-Capacity Solution?
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-1814">
            Let our enterprise specialists design a PRI solution for your
            organization.
          </p>
          <button onClick={() => onNavigate('contact')} className="bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg" data-id="element-1815">
            Speak with a Specialist
          </button>
        </div>
      </section>
    </div>;
}